/**
 * Splits a leave event into contiguous Mon–Fri segments so that the event bar
 * never renders on Saturday or Sunday columns in FullCalendar.
 *
 * IMPORTANT — date inclusivity contract:
 *   Backend leave dates (`startDate`, `endDate`) are INCLUSIVE on both ends.
 *   FullCalendar all-day events use EXCLUSIVE end. Every conversion from
 *   backend → FullCalendar must add +1 day to the last weekday of each
 *   segment. Do not bypass this utility for leave events.
 *
 * All date arithmetic is done in UTC to avoid timezone-induced date shifts
 * (e.g. Europe/Bucharest) that would occur when using toISOString() on local
 * midnight Date objects.
 */
export const splitIntoWeekdaySegments = (event) => {
  const segments = [];
  // Parse as UTC midnight to prevent timezone shifts
  const current = new Date(event.start + 'T00:00:00Z');
  const endDate = new Date(event.end + 'T00:00:00Z'); // inclusive from backend

  let segmentStart = null;
  let lastWeekday = null;

  const formatUTCDate = (date) => date.toISOString().slice(0, 10);

  const closeSegment = () => {
    const exclusiveEnd = new Date(lastWeekday);
    exclusiveEnd.setUTCDate(exclusiveEnd.getUTCDate() + 1);
    segments.push({
      ...event,
      start: formatUTCDate(segmentStart),
      end: formatUTCDate(exclusiveEnd),
    });
    segmentStart = null;
    lastWeekday = null;
  };

  while (current <= endDate) {
    const dow = current.getUTCDay(); // 0=Sun, 6=Sat
    if (dow !== 0 && dow !== 6) {
      if (!segmentStart) segmentStart = new Date(current);
      lastWeekday = new Date(current);
    } else if (segmentStart) {
      closeSegment();
    }
    current.setUTCDate(current.getUTCDate() + 1);
  }

  if (segmentStart) closeSegment();

  return segments;
};

/**
 * Builds the month-legend data from the formatted FullCalendar events held in
 * CalendarView state (already scoped to the visible month by fetchEvents).
 *
 * This is the inverse of splitIntoWeekdaySegments for display purposes: leave
 * segments are collapsed back into ONE row per leave request (deduped by
 * extendedProps.leaveRequestId) using the preserved INCLUSIVE
 * originalStart/originalEnd — never the segment's exclusive end.
 *
 * Returns ISO 'YYYY-MM-DD' strings only; the caller localizes them.
 *
 *   {
 *     holidays: [{ date, name }],                       // deduped, date-ascending
 *     leaves:   [{ leaveRequestId, employeeName,        // one row per request,
 *                  departmentName, leaveType, status,   // sorted by start then name
 *                  start, end }]                        // inclusive on both ends
 *   }
 */
export const buildMonthLegend = (events = []) => {
  const holidaysByDate = new Map();
  const leavesById = new Map();

  events.forEach((event) => {
    const ext = event.extendedProps || {};
    if (ext.type === 'HOLIDAY') {
      if (!holidaysByDate.has(event.start)) {
        holidaysByDate.set(event.start, { date: event.start, name: event.title });
      }
      return;
    }
    if (ext.type !== 'LEAVE') return;
    const id = ext.leaveRequestId;
    if (id === null || id === undefined || leavesById.has(id)) return;
    leavesById.set(id, {
      leaveRequestId: id,
      employeeName: ext.employeeName,
      departmentName: ext.departmentName,
      leaveType: ext.leaveType,
      status: ext.status,
      start: ext.originalStart,
      end: ext.originalEnd,
    });
  });

  const holidays = Array.from(holidaysByDate.values()).sort((a, b) =>
    a.date.localeCompare(b.date)
  );
  const leaves = Array.from(leavesById.values()).sort(
    (a, b) =>
      (a.start || '').localeCompare(b.start || '') ||
      (a.employeeName || '').localeCompare(b.employeeName || '')
  );

  return { holidays, leaves };
};

/**
 * Localized, UTC-pinned formatting for an inclusive 'YYYY-MM-DD' date.
 *
 * Parsing as UTC midnight (and formatting with `timeZone: 'UTC'`) prevents the
 * Europe/Bucharest shift that would otherwise move a local-midnight date back a
 * day. The month is abbreviated; the year is omitted by default (the surrounding
 * context — a visible month or range — implies it) and included only on request.
 *
 * Shared by CalendarView (legend + details modal) and DepartmentTimeOffOverview
 * so the two never drift into different date formats.
 */
export const formatCalendarDate = (isoDate, language, { withYear = false } = {}) =>
  new Date(isoDate + 'T00:00:00Z').toLocaleDateString(
    language === 'ro' ? 'ro-RO' : 'en-GB',
    { day: 'numeric', month: 'short', timeZone: 'UTC', ...(withYear && { year: 'numeric' }) }
  );

/**
 * Formats an inclusive [start, end] interval. Collapses to a single date when
 * start === end; otherwise renders "start – end" (en dash). Both ends are
 * inclusive, matching the backend leave-date contract.
 */
export const formatCalendarInterval = (start, end, language, options) =>
  start === end
    ? formatCalendarDate(start, language, options)
    : `${formatCalendarDate(start, language, options)} – ${formatCalendarDate(end, language, options)}`;
